import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  SpaceBetween,
  Button,
  Header,
  Container,
  FormField,
  Input,
  Textarea,
  DatePicker,
  RadioGroup,
  Checkbox,
  Alert,
  Modal,
  Box,
} from '@cloudscape-design/components';
import Icon from '@cloudscape-design/components/icon';
import { useQuery } from '@tanstack/react-query';
import { CreateCohortRequest } from '../api/cohorts';
import { programsApi } from '../api/programs';
import { useCreateCohort, useCohort, useUpdateCohort, useDeleteCohort /*, usePublishCohort TODO: Restore when isPublished flow is fully tested */ } from '../hooks/queries/useCohorts';
import { useProgram } from '../hooks/queries/usePrograms';
import { queryKeys } from '../lib/queryClient';
import { ErrorAlert } from '../components/ErrorAlert';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useBreadcrumbs } from '../contexts';
import { buildBreadcrumbs } from '../components/BreadcrumbBuilder';
import { UserSearchSelect } from '../components/UserSearchSelect';
import { UserSearchResult } from '../api/users';
import { PersonChip } from '../components/PersonChip';
import { useIsProgramAdminOrOwner } from '../hooks/usePermissions';
import { HelpPopover } from '../components/HelpPopover';
import { useNotifications } from '../components/NotificationFlashbar';

interface FormData {
  cohortName: string;
  description: string;
  wikiUrl: string;
  slackChannel: string;
  registrationOpenDate: string;
  registrationDeadline: string;
  startDate: string;
  endDate: string;
  maxParticipants: string;
  enableSelfEnrollment: boolean;
  mentorsEnabled: boolean;
  graduationApproval: 'automatic' | 'admin';
  visibility: string;
  isPublished: boolean;
}

interface FormErrors {
  cohortName?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  maxParticipants?: string;
  registrationOpenDate?: string;
  registrationDeadline?: string;
  orgLeader?: string;
}

const EMPTY_FORM: FormData = {
  cohortName: '',
  description: '',
  wikiUrl: '',
  slackChannel: '',
  registrationOpenDate: '',
  registrationDeadline: '',
  startDate: '',
  endDate: '',
  maxParticipants: '',
  enableSelfEnrollment: true,
  mentorsEnabled: false,
  graduationApproval: 'automatic',
  visibility: 'all',
  isPublished: false,
};

const DATE_ERRORS = {
  endDateOrder: 'Must be after start date',
  regDeadlineOrder: 'Must be after open date',
} as const;

const REQUIRED_FIELDS: Record<string, string> = {
  cohortName: 'Cohort name is required',
  registrationOpenDate: 'Registration open date is required when self-enrollment is enabled',
  registrationDeadline: 'Registration deadline is required when self-enrollment is enabled',
  startDate: 'Cohort start date is required',
  endDate: 'Cohort end date is required',
};

function CohortSettings() {
  const navigate = useNavigate();
  const { programId, cohortId } = useParams<{ programId?: string; cohortId?: string }>();
  const { setBreadcrumbs } = useBreadcrumbs();
  const isEditMode = !!cohortId;

  const [orgLeader, setOrgLeader] = useState<UserSearchResult | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const initialized = useRef(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  // Create mode: fetch program for breadcrumbs
  const { data: programForCreate } = useQuery({
    queryKey: queryKeys.programs.detail(programId!),
    queryFn: () => programsApi.get(programId!),
    enabled: !isEditMode && !!programId,
  });

  // Edit mode: fetch cohort to populate form, then its program for breadcrumbs
  const { data: cohort, isLoading: cohortLoading } = useCohort(isEditMode ? cohortId : undefined);
  const { data: programForEdit } = useProgram(cohort?.programId);

  const program = isEditMode ? programForEdit : programForCreate;
  const effectiveProgramId = isEditMode ? cohort?.programId : programId;

  // ── Mutations ──────────────────────────────────────────────────────────────

  const { mutateAsync: createCohort, isPending: creating, error: createError } = useCreateCohort();
  const { mutateAsync: updateCohort, isPending: updating, error: updateError } = useUpdateCohort();
  const { mutateAsync: deleteCohort, isPending: deleteLoading } = useDeleteCohort();
  // TODO: Restore when isPublished flow is fully tested
  // const { mutateAsync: publishCohort, isPending: publishLoading } = usePublishCohort();
  const { showSuccess } = useNotifications();
  const loading = creating || updating;
  const error = createError || updateError;

  // ── Delete state (edit mode only) ─────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [enrollmentCount, setEnrollmentCount] = useState<number>(0);
  // TODO: Restore when isPublished flow is fully tested
  // const [showPublishModal, setShowPublishModal] = useState(false);
  // const [publishConfirmText, setPublishConfirmText] = useState('');

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteError(null);
    setShowDeleteModal(true);
  };
  // ── Populate form in edit mode ─────────────────────────────────────────────
  // initialized ref prevents background refetches from clobbering in-progress edits

  useEffect(() => {
    if (isEditMode && cohort && !initialized.current) {
      initialized.current = true;
      setFormData({
        cohortName: cohort.cohortName,
        description: cohort.description || '',
        wikiUrl: cohort.wikiUrl || '',
        slackChannel: cohort.slackChannel || '',
        registrationOpenDate: cohort.registrationStartDate || '',
        registrationDeadline: cohort.registrationEndDate || '',
        startDate: cohort.startDate,
        endDate: cohort.endDate,
        maxParticipants: cohort.capacity ? cohort.capacity.toString() : '',
        enableSelfEnrollment: cohort.selfEnrollment ?? true,
        mentorsEnabled: cohort.mentorsEnabled ?? false,
        graduationApproval: cohort.graduationApproval ?? 'automatic',
        visibility: cohort.visibility === 'public' ? 'all' : 'restricted',
        isPublished: cohort.isPublished ?? false,
      });
      setOrgLeader(cohort.orgLeader
        ? { login: cohort.orgLeader, name: cohort.orgLeaderName ?? cohort.orgLeader, firstName: '', lastName: '', email: '', title: '' }
        : null
      );
      setEnrollmentCount(cohort.enrollmentCount ?? 0);
    }
  }, [isEditMode, cohort]);

  // ── Breadcrumbs ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isEditMode && cohort && program) {
      setBreadcrumbs(buildBreadcrumbs({
        programId: program.programId,
        programName: program.programName,
        cohortId: cohort.cohortId,
        cohortName: cohort.cohortName,
        currentPage: 'Edit Cohort',
      }));
    } else if (!isEditMode && program) {
      setBreadcrumbs(buildBreadcrumbs({
        programId: program.programId,
        programName: program.programName,
        currentPage: 'Create Cohort',
      }));
    }
    return () => setBreadcrumbs([]);
  }, [isEditMode, cohort, program, setBreadcrumbs]);

  // ── Field update with inline validation ───────────────────────────────────

  const updateField = (field: keyof FormData, value: string | boolean, errorKey?: keyof FormErrors) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    setErrors(prev => {
      const next = { ...prev };

      if (errorKey) {
        if (!value && REQUIRED_FIELDS[field]) {
          next[errorKey] = REQUIRED_FIELDS[field];
        } else if (field === 'cohortName' && value && !/^[a-zA-Z0-9\s\-_]+$/.test(value as string)) {
          next[errorKey] = 'Name can only contain letters, numbers, spaces, dashes, and underscores';
        } else if (field === 'cohortName' && (value as string).length > 200) {
          next[errorKey] = 'Must be 200 characters or less';
        } else if (next[errorKey]) {
          delete next[errorKey];
        }
      }

      if (field === 'maxParticipants') {
        const val = (value as string).trim();
        if (val && parseInt(val, 10) <= 0) {
          next.maxParticipants = 'Must be greater than 0';
        } else {
          delete next.maxParticipants;
        }
      }

      if (field === 'registrationOpenDate' || field === 'registrationDeadline') {
        const open = field === 'registrationOpenDate' ? (value as string) : newFormData.registrationOpenDate;
        const deadline = field === 'registrationDeadline' ? (value as string) : newFormData.registrationDeadline;
        if (open && deadline && open > deadline) {
          next.registrationDeadline = DATE_ERRORS.regDeadlineOrder;
        } else if (next.registrationDeadline === DATE_ERRORS.regDeadlineOrder) {
          delete next.registrationDeadline;
        }
      }

      if (field === 'startDate' || field === 'endDate') {
        const start = field === 'startDate' ? (value as string) : newFormData.startDate;
        const end = field === 'endDate' ? (value as string) : newFormData.endDate;
        if (start && end && start > end) {
          next.endDate = DATE_ERRORS.endDateOrder;
        } else if (next.endDate === DATE_ERRORS.endDateOrder) {
          delete next.endDate;
        }
      }

      return next;
    });
  };

  // ── Form validation ────────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.cohortName.trim()) {
      newErrors.cohortName = 'Cohort name is required';
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(formData.cohortName)) {
      newErrors.cohortName = 'Name can only contain letters, numbers, spaces, dashes, and underscores';
    } else if (formData.cohortName.length > 200) {
      newErrors.cohortName = 'Must be 200 characters or less';
    }
    if (formData.description.length > 2000) {
      newErrors.description = 'Must be 2000 characters or less';
    }
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = DATE_ERRORS.endDateOrder;
    }
    if (formData.maxParticipants.trim() && parseInt(formData.maxParticipants, 10) <= 0) {
      newErrors.maxParticipants = 'Must be greater than 0';
    }
    if (!formData.registrationOpenDate && formData.enableSelfEnrollment) newErrors.registrationOpenDate = 'Registration open date is required when self-enrollment is enabled';
    if (!formData.registrationDeadline && formData.enableSelfEnrollment) newErrors.registrationDeadline = 'Registration deadline is required when self-enrollment is enabled';
    if (formData.registrationOpenDate && formData.registrationDeadline && formData.registrationOpenDate > formData.registrationDeadline) {
      newErrors.registrationDeadline = DATE_ERRORS.regDeadlineOrder;
    }
    if (formData.visibility === 'restricted' && !orgLeader) {
      newErrors.orgLeader = 'Org leader is required when visibility is restricted';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditMode && cohort) {
        await updateCohort({
          cohortId: cohort.cohortId,
          data: {
            programId: cohort.programId,
            cohortName: formData.cohortName,
            description: formData.description || '',
            wikiUrl: formData.wikiUrl || null,
            slackChannel: formData.slackChannel || null,
            startDate: formData.startDate,
            endDate: formData.endDate,
            registrationOpenDate: formData.registrationOpenDate || undefined,
            registrationDeadline: formData.registrationDeadline || undefined,
            capacity: formData.maxParticipants.trim() ? parseInt(formData.maxParticipants, 10) : null,
            visibility: formData.visibility === 'all' ? 'public' : 'org-restricted',
            orgLeader: formData.visibility === 'all' ? null : orgLeader?.login,
            orgLeaderName: formData.visibility === 'all' ? null : orgLeader?.name,
            mentorsEnabled: formData.mentorsEnabled,
            selfEnrollment: formData.enableSelfEnrollment,
            graduationApproval: formData.graduationApproval,
          },
        });
        navigate(`/cohorts/${cohort.cohortId}`);
      } else {
        const cohortData: CreateCohortRequest = {
          programId: effectiveProgramId!,
          cohortName: formData.cohortName,
          description: formData.description || '',
          startDate: formData.startDate,
          endDate: formData.endDate,
          registrationOpenDate: formData.registrationOpenDate || undefined,
          registrationDeadline: formData.registrationDeadline || undefined,
          wikiUrl: formData.wikiUrl || undefined,
          slackChannel: formData.slackChannel || undefined,
          capacity: formData.maxParticipants.trim() ? parseInt(formData.maxParticipants, 10) : undefined,
          visibility: formData.visibility === 'all' ? 'public' : 'org-restricted',
          selfEnrollment: formData.enableSelfEnrollment,
          mentorsEnabled: formData.mentorsEnabled,
          graduationApproval: formData.graduationApproval,
          orgLeader: orgLeader?.login,
          orgLeaderName: orgLeader?.name,
        };
        const created = await createCohort(cohortData);
        navigate(`/cohorts/${created.cohortId}`);
      }
    } catch (err) {
      console.error('Failed to save cohort:', err);
    }
  };

  const isProgramAdmin = useIsProgramAdminOrOwner(effectiveProgramId);

  // ── Loading / not-found guards (edit mode only) ────────────────────────────

  if (isEditMode && cohortLoading) {
    return <LoadingSpinner message="Loading cohort..." />;
  }

  if (isEditMode && !cohort) {
    return <ErrorAlert error="Cohort not found" />;
  }

  // ── Permission guard ───────────────────────────────────────────────────────
  // Requires program admin for the relevant program. In create mode we know
  // the programId from the URL; in edit mode we derive it from the cohort.

  const canAccess = !!effectiveProgramId && isProgramAdmin;

  return (
    <ProtectedRoute canAccess={canAccess}>
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="link"
            onClick={() => navigate(isEditMode ? `/cohorts/${cohortId}` : `/programs/${effectiveProgramId}`)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading}>
            {isEditMode ? 'Save changes' : 'Create Cohort'}
          </Button>
        </SpaceBetween>
      }
      header={
        <Header
          description={isEditMode ? 'Update cohort details and settings.' : 'Create a new cohort for this training program.'}
          variant="h1"
          info={!isEditMode && (
            <HelpPopover
              header="Cohorts"
              content={
                <>
                  A cohort is a single run of a training program — a defined group of participants going through the program together over a set period of time.
                  <br /><br />
                  One program can have multiple cohorts (e.g. Spring 2025, Fall 2025). Each cohort has its own timeline, enrollment settings, participants, and activities.
                  <br /><br />
                  <strong>You must be a program owner or admin to create cohorts.</strong>
                </>
              }
            />
          )}
        >
          {isEditMode ? 'Cohort Settings' : 'Create Cohort'}
        </Header>
      }
      errorText={undefined}
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" header={isEditMode ? 'Unable to save cohort' : 'Unable to create cohort'}>
            {error.message}
          </Alert>
        )}
        {/* TODO: Uncomment when isPublished flow is fully tested
        {isEditMode && !formData.isPublished && (
          <Alert
            type="info"
            header="This cohort is not yet published"
            action={
              <Button variant="primary" onClick={() => { setPublishConfirmText(''); setShowPublishModal(true); }}>
                Publish cohort
              </Button>
            }
          >
            It is only visible to program admins and owners. Publish it when you are ready for users to discover and enroll.
          </Alert>
        )}
        */}

        <Container>
          <SpaceBetween size="l">

            {/* ── Identity ── */}
            <FormField
              description="Enter a unique name for this cohort."
              label="Cohort name *"
              errorText={errors.cohortName}
            >
              <Input
                value={formData.cohortName}
                onChange={(event) => updateField('cohortName', event.detail.value, 'cohortName')}
              />
            </FormField>
            <FormField
              description="Provide a brief description of this cohort's goals and audience."
              label="Description — optional"
              stretch
              errorText={errors.description}
            >
              <Textarea
                rows={3}
                value={formData.description}
                onChange={(event) => {
                  const val = event.detail.value;
                  setFormData({ ...formData, description: val });
                  if (val.length > 2000) {
                    setErrors(prev => ({ ...prev, description: 'Must be 2000 characters or less' }));
                  } else {
                    setErrors(prev => { const next = { ...prev }; delete next.description; return next; });
                  }
                }}
              />
            </FormField>
            <FormField label="Wiki URL — optional" description="Link to the cohort wiki page for additional information and resources.">
              <Input
                type="url"
                value={formData.wikiUrl}
                onChange={(event) => setFormData({ ...formData, wikiUrl: event.detail.value })}
              />
            </FormField>
            <FormField label="Slack channel URL — optional" description="Link to the cohort's Slack channel for communication.">
              <Input
                type="url"
                value={formData.slackChannel}
                onChange={(event) => setFormData({ ...formData, slackChannel: event.detail.value })}
              />
            </FormField>

            {/* ── Timeline ── */}
            <div style={{ display: 'flex', gap: '24px', maxWidth: '500px' }}>
              <div style={{ flex: 1 }}>
                <FormField label="Start date *" errorText={errors.startDate}>
                  <DatePicker
                    value={formData.startDate}
                    onChange={(event) => updateField('startDate', event.detail.value, 'startDate')}
                  />
                </FormField>
              </div>
              <div style={{ flex: 1 }}>
                <FormField label="End date *" errorText={errors.endDate}>
                  <DatePicker
                    value={formData.endDate}
                    onChange={(event) => updateField('endDate', event.detail.value, 'endDate')}
                  />
                </FormField>
              </div>
            </div>

            {/* ── Enrollment ── */}
            <FormField label="Self-enrollment" description="When enabled, participants can register themselves during the registration window." stretch>
              <Checkbox
                checked={formData.enableSelfEnrollment}
                onChange={(event) => {
                  const checked = event.detail.checked;
                  setFormData(prev => ({ ...prev, enableSelfEnrollment: checked }));
                  if (!checked) {
                    setErrors(prev => {
                      const next = { ...prev };
                      delete next.registrationOpenDate;
                      delete next.registrationDeadline;
                      return next;
                    });
                  }
                }}
              >
                Allow participants to self-enroll
              </Checkbox>
            </FormField>
            {formData.enableSelfEnrollment && (
              <div style={{ display: 'flex', gap: '24px', maxWidth: '500px' }}>
                <div style={{ flex: 1 }}>
                  <FormField label="Registration open date *" errorText={errors.registrationOpenDate} description="When self-enrollment opens.">
                    <DatePicker
                      value={formData.registrationOpenDate}
                      onChange={(event) => updateField('registrationOpenDate', event.detail.value, 'registrationOpenDate')}
                    />
                  </FormField>
                </div>
                <div style={{ flex: 1 }}>
                  <FormField label="Registration deadline *" errorText={errors.registrationDeadline} description="When self-enrollment closes.">
                    <DatePicker
                      value={formData.registrationDeadline}
                      onChange={(event) => updateField('registrationDeadline', event.detail.value, 'registrationDeadline')}
                    />
                  </FormField>
                </div>
              </div>
            )}
            <FormField
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  Enrollment eligibility
                  <HelpPopover
                    header="Enrollment Eligibility"
                    content={
                      <>
                        <strong>Open to all</strong> — any user can enroll in this cohort (subject to self-enrollment being enabled).
                        <br /><br />
                        <strong>Restricted to org leader or manager reports</strong> — enrollment is limited to users within the selected org leader's reporting chain. The cohort is still visible to everyone.
                      </>
                    }
                  />
                </span>
              }
              description="Restrict enrollment to users within a specific org leader's reporting chain."
              stretch
            >
              <RadioGroup
                items={[
                  { label: 'Open to all', value: 'all' },
                  { label: 'Restricted to org leader or manager reports', value: 'restricted' },
                ]}
                value={formData.visibility}
                onChange={(event) => {
                  setFormData({ ...formData, visibility: event.detail.value });
                  if (event.detail.value === 'all') {
                    setOrgLeader(null);
                    setErrors(prev => { const next = { ...prev }; delete next.orgLeader; return next; });
                  }
                }}
              />
            </FormField>
            {formData.visibility === 'restricted' && (
              <FormField
                description="Search for the org leader or manager whose reports should have access."
                label="Org leader *"
                errorText={errors.orgLeader}
              >
                <UserSearchSelect
                  onSelect={(user) => {
                    setOrgLeader(user);
                    setErrors(prev => { const next = { ...prev }; delete next.orgLeader; return next; });
                  }}
                  clearOnSelect={false}
                  managersOnly={true}
                  placeholder="Search by name or alias..."
                />
                {orgLeader && (
                  <PersonChip
                    login={orgLeader.login}
                    name={orgLeader.name}
                    subtitle={orgLeader.title || undefined}
                    onRemove={() => setOrgLeader(null)}
                    marginTop="8px"
                  />
                )}
              </FormField>
            )}
            <FormField
              description="Leave blank for unlimited enrollment."
              label="Max participants — optional"
              errorText={errors.maxParticipants}
            >
              <div style={{ maxWidth: '200px' }}>
                <Input
                  type="number"
                  value={formData.maxParticipants}
                  onChange={(event) => updateField('maxParticipants', event.detail.value)}
                />
              </div>
            </FormField>

            {/* ── Cohort structure ── */}
            <FormField
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  Mentors
                  <HelpPopover
                    header="Mentors"
                    content={
                      <>
                        <strong>Without mentors</strong>, the cohort is self-paced. Admins manage all settings, activities, and graduations. Activities can be marked complete by participants or admins, but there is no mentor review step.
                        <br /><br />
                        <strong>With mentors enabled</strong>, mentors can request to sign-up for the cohort. Admins approve or deny mentor requests and assign mentors to participants. Individual activities can then be configured to require mentor review — useful for things like code reviews or project submissions where direct feedback from a subject matter expert is needed.
                      </>
                    }
                  />
                </span>
              }
              description="Enable mentor assignments and mentor-reviewed activities for this cohort."
              stretch
            >
              <Checkbox
                checked={formData.mentorsEnabled}
                onChange={(event) => setFormData(prev => ({ ...prev, mentorsEnabled: event.detail.checked }))}
              >
                Enable mentor assignments
              </Checkbox>
            </FormField>
            <FormField
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  Graduation approval
                  <HelpPopover
                    header="Graduation Approval"
                    content={
                      <>
                        <strong>Automatic</strong> — a participant is graduated as soon as all required activities are marked complete. No admin action needed.
                        <br /><br />
                        <strong>Admin approval</strong> — a program admin must review and approve each graduation request before the participant is marked as graduated. Use this when you want a final sign-off step regardless of activity completion.
                      </>
                    }
                  />
                </span>
              }
              description="How participants are graduated from this cohort."
              stretch
            >
              <RadioGroup
                items={[
                  { label: 'Automatic — graduate immediately when all required activities are complete', value: 'automatic' },
                  { label: 'Admin — program admin must approve graduation', value: 'admin' },
                ]}
                value={formData.graduationApproval}
                onChange={(event) => setFormData(prev => ({ ...prev, graduationApproval: event.detail.value as 'automatic' | 'admin' }))}
              />
            </FormField>

          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Form>

          {/* Danger Zone — edit mode only */}
          {isEditMode && (
            <div style={{ marginTop: '24px' }}>
              <Container
                header={
                  <Header
                    variant="h2"
                    description="These actions affect cohort existence and cannot be undone."
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="status-warning" variant="error" />
                      Danger Zone
                    </span>
                  </Header>
                }
              >
                <div>
                  <Button
                    variant="inline-link"
                    onClick={openDeleteModal}
                    disabled={enrollmentCount > 0}
                  >
                    Delete Cohort
                  </Button>
                  <Box color="text-body-secondary" fontSize="body-s">
                    {enrollmentCount > 0
                      ? `This cohort has ${enrollmentCount} enrolled participant${enrollmentCount === 1 ? '' : 's'}. Remove all participants before deleting.`
                      : 'Permanently delete this cohort and all associated activities and settings.'
                    }
                  </Box>
                </div>
              </Container>
            </div>
          )}

          {/* TODO: Uncomment when isPublished flow is fully tested
          Publish Cohort Modal
          {isEditMode && cohort && (
            <Modal
              visible={showPublishModal}
              onDismiss={() => setShowPublishModal(false)}
              header="Publish Cohort"
              footer={
                <Box float="right">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => setShowPublishModal(false)}>Cancel</Button>
                    <Button
                      variant="primary"
                      loading={publishLoading}
                      disabled={publishConfirmText !== 'confirm'}
                      onClick={async () => {
                        try {
                          await publishCohort(cohort.cohortId);
                          setFormData(prev => ({ ...prev, isPublished: true }));
                          setShowPublishModal(false);
                        } catch (err) {
                          console.error('Failed to publish cohort:', err);
                        }
                      }}
                    >
                      Publish
                    </Button>
                  </SpaceBetween>
                </Box>
              }
            >
              <SpaceBetween size="m">
                <Alert type="warning">
                  Once published, this cohort will be "live" and visible to all users. This cannot be undone.
                </Alert>
                <FormField label={<>Type <strong>confirm</strong> to proceed</>}>
                  <Input
                    value={publishConfirmText}
                    onChange={({ detail }) => setPublishConfirmText(detail.value)}
                    placeholder="confirm"
                    autoComplete="off"
                  />
                </FormField>
              </SpaceBetween>
            </Modal>
          )}
          */}

          {/* Delete Cohort Modal */}
          {isEditMode && cohort && (
            <Modal
              visible={showDeleteModal}
              onDismiss={() => setShowDeleteModal(false)}
              header="Delete Cohort"
              footer={
                <Box float="right">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button
                      variant="primary"
                      loading={deleteLoading}
                      disabled={deleteConfirmText !== 'confirm'}
                      onClick={async () => {
                        try {
                          setDeleteError(null);
                          await deleteCohort({ cohortId: cohort.cohortId, programId: cohort.programId });
                          showSuccess('Cohort deleted', `${cohort.cohortName} has been permanently deleted.`);
                          navigate(`/programs/${cohort.programId}`);
                        } catch (err: any) {
                          setDeleteError(err.message || 'Failed to delete cohort');
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </SpaceBetween>
                </Box>
              }
            >
              <SpaceBetween size="m">
                {deleteError && (
                  <Alert type="error" dismissible onDismiss={() => setDeleteError(null)}>{deleteError}</Alert>
                )}
                <Alert type="warning">
                  Are you sure you want to permanently delete <strong>{cohort.cohortName}</strong>?
                  This will remove all associated activities and settings and cannot be undone.
                </Alert>
                <FormField label={<>Type <strong>confirm</strong> to proceed</>}>
                  <Input
                    value={deleteConfirmText}
                    onChange={({ detail }) => setDeleteConfirmText(detail.value)}
                    placeholder="confirm"
                    autoComplete="off"
                  />
                </FormField>
              </SpaceBetween>
            </Modal>
          )}
    </ProtectedRoute>
  );
}

export default CohortSettings;
